/** @jsxImportSource @emotion/react */
import { FC, memo, useState } from 'react';
import { Box, css } from '@mui/material';
import itemPlaceholder from 'assets/itemPlaceholder.png';
import itemPlaceholder2x from 'assets/itemPlaceholder@2x.png';
import { useHighPixelDensity } from 'hooks/useHighPixelDensity';

type Props = {
  urls: string[];
  name?: string;
  width?: number;
  height?: number;
};

/**
 * Dish photo: a single image for a regular item, a collage for a combo.
 * The placeholder is the tile's own background, so it also shows through for a
 * dish without a photo and for one whose photo fails to load.
 */
const DishImageTile: FC<Props> = ({
  urls,
  name = '',
  width = 104,
  height = 84,
}) => {
  const isHighPixelDensity = useHighPixelDensity();
  const [failedUrls, setFailedUrls] = useState<string[]>([]);

  const cells = urls.slice(0, 4);
  const extra = urls.length - 3;
  const showExtra = urls.length > 4;

  return (
    <Box
      css={css`
        display: grid;
        gap: 1px;
        width: ${width}px;
        height: ${height}px;
        grid-template-columns: ${cells.length === 1 ? '1fr' : '1fr 1fr'};
        grid-template-rows: ${cells.length <= 2 ? '1fr' : '1fr 1fr'};
        border-radius: 4px;
        overflow: hidden;
        background: url(${isHighPixelDensity
            ? itemPlaceholder2x
            : itemPlaceholder})
          center/cover no-repeat;
      `}
    >
      {cells.map((url, index) => (
        <Box
          key={`${url}-${index}`}
          css={css`
            position: relative;
            overflow: hidden;
            ${cells.length === 3 && index === 0 ? 'grid-row: 1 / span 2;' : ''}
          `}
        >
          <img
            src={url}
            alt={name}
            loading="lazy"
            onError={() => setFailedUrls((failed) => [...failed, url])}
            css={css`
              display: block;
              width: 100%;
              height: 100%;
              object-fit: cover;
              /* a photo that fails to load gives way to the placeholder behind it,
                 instead of showing the browser's broken-image icon and alt text */
              visibility: ${failedUrls.includes(url) ? 'hidden' : 'visible'};
            `}
          />
          {showExtra && index === 3 && (
            <Box
              css={css`
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(0, 0, 0, 0.45);
                color: #fff;
                font-size: 13px;
                font-weight: 600;
              `}
            >
              +{extra}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default memo(DishImageTile);
